import { useEffect, useState } from 'react'

import { Pencil, TrashBin } from '@gravity-ui/icons'
import { Button, toast } from '@heroui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { reportsTimeApi } from '@/api/reportsTime.api'
import { ConfirmDeleteDialog } from '@/components/ui/ConfirmDeleteDialog'
import { PageHeader } from '@/components/ui/PageHeader'
import { useTimesheetStore } from '@/store/timesheet.store'

import { CalendarSidebar } from '../components/CalendarSidebar'
import { DayActivitiesCard } from '../components/DayActivitiesCard'
import { DayDetailsCard } from '../components/DayDetailsCard'

export default function TimesheetView() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const { id } = useParams<{ id: string }>()
  const report = useTimesheetStore(s => s.report)
  const fetchData = useTimesheetStore(s => s.fetchData)
  const [isInitializing, setIsInitializing] = useState(!!id)

  useEffect(() => {
    async function init() {
      if (id) {
        try {
          const report = await reportsTimeApi.getTimeReport(id)
          useTimesheetStore.setState({
            reportId: id,
            report,
            filters: {
              driverID: report.Driver?.ID,
              year: report.Year,
              month: report.Month,
            },
            selectedDay: null,
          })
        } catch (error) {
          console.error('Failed to fetch time report for editing', error)
        }
      } else {
        useTimesheetStore.setState({
          reportId: undefined,
          report: undefined,
          filters: {
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            driverID: undefined,
          },
          selectedDay: null,
        })
      }
      setIsInitializing(false)
      fetchData()
    }
    init()
  }, [id, fetchData])

  if (isInitializing) {
    return <div className="p-5">Загрузка...</div>
  }

  const handleDelete = async () => {
    if (!id) return
    setIsDeleting(true)
    try {
      await reportsTimeApi.deleteTimeReport(id)
      toast.success(t('timesheet.toast.delete_success'))
      navigate('/timesheet')
    } catch (err) {
      toast.danger(t('timesheet.toast.delete_error'), { description: (err as Error).message })
    } finally {
      setIsDeleting(false)
      setIsDeleteOpen(false)
    }
  }

  const driverName = report?.Driver?.Name || ''
  const formattedMonthYear = report
    ? new Date(report.Year, report.Month - 1).toLocaleString(i18n.resolvedLanguage === 'ru' ? 'ru-RU' : 'en-US', { month: 'long', year: 'numeric' })
    : ''
  const pageTitle = report ? `Табель ${driverName} ${formattedMonthYear}` : t('nav.timesheet')

  return (
    <div className="p-5 h-full flex flex-col space-y-5">
      <div className="flex justify-between items-start">
        <PageHeader
          onBack={() => navigate('/timesheet')}
          title={pageTitle}
          subtitle={t('timesheet.subtitle')}
        />
        <div className="flex gap-2 items-center">
          <Button variant="primary" onPress={() => navigate(`/timesheet/${id}/edit`)}>
            <Pencil className="size-4" />
            {t('common.edit')}
          </Button>
          {id && (
            <Button
              variant="danger-soft"
              onPress={() => setIsDeleteOpen(true)}
            >
              <TrashBin className="size-4" />
              {t('common.delete')}
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 flex gap-5 overflow-hidden min-h-0">
        <CalendarSidebar />

        <div className="flex-1 flex flex-row gap-5 min-h-0">
          <div className="flex-1 min-h-0 max-h-[85vh]">
            <DayDetailsCard />
          </div>
          <div className="flex-2 min-h-0 max-h-[85vh]">
            <DayActivitiesCard isReadOnly />
          </div>
        </div>
      </div>

      <ConfirmDeleteDialog
        isOpen={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={handleDelete}
        title={t('common.delete')}
        description={t('timesheet.delete_confirm')}
        isLoading={isDeleting}
      />
    </div>
  )
}
